import { Stack, useRouter } from "expo-router";
import { PropsWithChildren, ReactNode, useEffect } from "react";
import { usePalette } from "@/utils/usePalette";
import { useMobileBreakpoint } from "@/utils/useMobileBreakpoint";
import { useNavigationTitleHistoryActions } from "@/utils/useNavigationTitleHistory";
import { StackLayout } from "../dashboard/stack.layout";
import {
  Descriptor,
  NavigationHelpers,
  NavigationProp,
  ParamListBase,
  RouteProp,
  StackNavigationState,
} from "@react-navigation/native";
import {
  NativeStackNavigationOptions,
  NativeStackNavigationEventMap,
} from "@react-navigation/native-stack";

type ProxyLayoutProps = {
  state: StackNavigationState<ParamListBase>;
  navigation: NavigationHelpers<ParamListBase, {}>;
  descriptors: Record<
    string,
    Descriptor<
      NativeStackNavigationOptions,
      NavigationProp<
        ParamListBase,
        string,
        string | undefined,
        StackNavigationState<ParamListBase>,
        NativeStackNavigationOptions,
        NativeStackNavigationEventMap
      >,
      RouteProp<ParamListBase, string>
    >
  >;
  children: ReactNode;
};

function ProxyLayout({ descriptors, children }: ProxyLayoutProps) {
  const actions = useNavigationTitleHistoryActions();

  useEffect(() => {
    const opts: Parameters<typeof actions.set>[0] = {};
    for (const [key, d] of Object.entries(descriptors)) {
      opts[key] = {
        title:
          typeof d.options.headerTitle === "string"
            ? d.options.headerTitle || d.options.title
            : d.options.title,
      };
    }

    actions.set(opts);
  }, [descriptors]);

  return <>{children}</>;
}

export function MobileHeader(
  props: Omit<PropsWithChildren<typeof Stack>, "$$typeof" | "Screen">
) {
  const breakpoint = useMobileBreakpoint();
  const palette = usePalette();
  const actions = useNavigationTitleHistoryActions();

  return (
    <Stack
      screenListeners={{
        beforeRemove: (e) => {
          if (!e.defaultPrevented && e.target) {
            actions.delete(e.target);
          }
        },
      }}
      screenOptions={{
        headerShown: breakpoint === "mobile",
        headerTintColor: palette["--color-typography-900"],
        headerStyle: {
          backgroundColor: palette["--color-background-0"],
        },
      }}
      screenLayout={StackLayout}
      layout={ProxyLayout}
    >
      {props.children}
    </Stack>
  );
}
